import { proxied } from '../services/assets';
import { API_BASE, IS_MOBILE } from '../config';
import { SR } from './beatGrid';
import { planCuts } from './stageCuts';

// songAnalysis — a stage song, measured and cut, for scene view at the plaza
// (city/beatGrid measures, city/stageCuts plans the cuts).
//
// THROUGH THE API'S RELAY: the browser may only read a song's samples with CORS,
// which `/api/assets/file` carries and the tracks bucket does not for the
// World's domain (services/assets `proxied`). Decoded at 11 kHz mono (an
// OfflineAudioContext at that rate resamples while it decodes), measured in a
// worker so a live set never freezes for the second it takes.
//
// One song at a time, each once (cached by its link), and only for someone
// watching in scene view: the current song, and the next one while this plays.
// A song that cannot be fetched or measured stays unmeasured and scene view
// keeps its steady cutting for it.
//
// ⚠ THE SERVER'S MEASUREMENT FIRST, ON EVERY DEVICE (2026-11-04). Khari, on phones
// never getting a beat: *"Why is this measurement done on device?"* The API now
// measures each stage song once (CityStageService queueBeatGrids, the same
// beatGrid code) and serves it at GET /api/world/beat/:trackId, so a phone gets
// the beat without decoding anything and every viewer snaps to the same bar.
// Only a song the server has not measured yet is measured here, desktop only,
// as before; the server is asked again a few minutes later.

const cache = new Map();   // url → { state: 'queued' | 'ready' | 'failed', A?, plans? (by feel), retryAt? }
const SERVER_RETRY_MS = 5 * 60000;   // an unmeasured song: ask the server again after this
const queue = [];
let busy = false;

let worker; let nextId = 0;
const pending = new Map();
function measureOffThread(samples) {
  if (worker === undefined) {
    try {
      worker = new Worker(new URL('./beatGrid.worker.js', import.meta.url), { type: 'module' });
      worker.onmessage = (e) => { const done = pending.get(e.data?.id); if (done) { pending.delete(e.data.id); done(e.data.result || null); } };
      worker.onerror = () => { for (const done of pending.values()) done(null); pending.clear(); worker = null; };
    } catch { worker = null; }
  }
  // No worker (an old browser): stay unmeasured rather than freeze the set.
  if (!worker) return Promise.resolve(null);
  return new Promise((resolve) => {
    const id = ++nextId;
    pending.set(id, resolve);
    worker.postMessage({ id, samples }, [samples.buffer]);
  });
}

/** Decode to mono at SR. Safari's offline context may refuse 11 kHz: then 22/44 kHz, averaged down. */
async function decodeMono(buf) {
  const Ctx = typeof window !== 'undefined' && (window.OfflineAudioContext || window.webkitOfflineAudioContext);
  if (!Ctx) return null;
  for (const k of [1, 2, 4]) {
    let ctx;
    try { ctx = new Ctx(1, SR * k, SR * k); } catch { continue; }
    const audio = await new Promise((res, rej) => {
      const p = ctx.decodeAudioData(buf.slice(0), res, rej);
      if (p && p.then) p.then(res, rej);
    });
    const ch = audio.numberOfChannels; const len = Math.floor(audio.length / k);
    const x = new Float32Array(len);
    for (let c = 0; c < ch; c++) {
      const d = audio.getChannelData(c);
      for (let i = 0; i < len; i++) { let s = 0; for (let j = 0; j < k; j++) s += d[i * k + j]; x[i] += s / (k * ch); }
    }
    return x;
  }
  return null;
}

async function pump() {
  if (busy) return;
  const job = queue.shift();
  if (!job) return;
  busy = true;
  try {
    const fromServer = job.trackId ? await serverGrid(job.trackId) : null;
    if (fromServer) {
      cache.set(job.url, { state: 'ready', A: fromServer, seed: job.seed, plans: new Map(), server: true });
      return;
    }
    if (IS_MOBILE) {   // never decoded on a phone (the 2026-10-29 stutter): wait for the server's
      cache.set(job.url, { state: 'failed', retryAt: Date.now() + SERVER_RETRY_MS });
      return;
    }
    const res = await fetch(proxied(job.url), { credentials: 'omit' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const x = await decodeMono(await res.arrayBuffer());
    const A = x ? await measureOffThread(x) : null;
    cache.set(job.url, A ? { state: 'ready', A, seed: job.seed, plans: new Map() } : { state: 'failed', retryAt: job.trackId ? Date.now() + SERVER_RETRY_MS : null });
  } catch (err) {
    cache.set(job.url, { state: 'failed', retryAt: job.trackId ? Date.now() + SERVER_RETRY_MS : null });
    if (import.meta.env.DEV) console.warn('[stage] could not measure a song:', err?.message || err);
  } finally {
    busy = false;
    pump();
  }
}

/** The server's measurement of a track, or null (not measured yet, or unreachable). */
async function serverGrid(trackId) {
  try {
    const res = await fetch(`${API_BASE}/api/world/beat/${encodeURIComponent(trackId)}`, { credentials: 'omit' });
    if (!res.ok) return null;
    const j = await res.json();
    return j?.grid && Array.isArray(j.grid.bars) && Array.isArray(j.grid.beats) ? j.grid : null;
  } catch { return null; }
}

/** A seed from the link when there is no track id (the same song always cuts the same way). */
function seedOf(url) { let h = 2166136261; for (let i = 0; i < url.length; i++) { h ^= url.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }

/** One plan per feel: the song is measured once, cut again if its feel arrives or changes. */
const feelKey = (f) => (f ? `${f.motion?.energy || '-'}:${f.motion?.faceTime || '-'}:${f.sections?.length || 0}:${f.sung?.length || 0}` : 'none');

/**
 * The song's measurement and cuts — { A, plan } — or null while it is being
 * measured (asking starts it) or when it could not be.
 * @param url   the song's link (shows[].tracks[].url)
 * @param seed  its track id, when known
 * @param feel  how it wants to be filmed (shows[].tracks[].feel), or null
 */
export function songCuts(url, seed, feel = null) {
  // ⚠ NOT DECODED ON A PHONE (2026-10-29): measuring decodes the WHOLE song in
  // the browser (fetch + decodeAudioData + a copy: tens of MB per song, twice
  // per set with the next one) and the plaza stuttered on an iPhone. A phone
  // takes the server's measurement or nothing (pump); without a track id there
  // is no server measurement to ask for.
  const trackId = Number(seed) > 0 ? Number(seed) : null;
  if (!url || (IS_MOBILE && !trackId)) return null;
  let e = cache.get(url);
  if (e?.state === 'failed' && e.retryAt && Date.now() > e.retryAt) { cache.delete(url); e = null; }
  if (e) {
    if (e.state !== 'ready') return null;
    const k = feelKey(feel);
    if (!e.plans.has(k)) e.plans.set(k, planCuts(e.A, e.seed, feel));
    const plan = e.plans.get(k);
    return plan ? { A: e.A, plan } : null;
  }
  cache.set(url, { state: 'queued' });
  queue.push({ url, seed: Number(seed) || seedOf(url), trackId });
  while (cache.size > 16) cache.delete(cache.keys().next().value);
  pump();
  return null;
}

if (import.meta.env.DEV && typeof window !== 'undefined') window.__songCuts = cache;   // probes only
